/**
 * Helper to convert hex to RGB.
 * @param {string} hex - The hex color string.
 * @returns {any} The RGB object.
 */
export function hexToRgb(hex: string) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const normalizedHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalizedHex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Helper to convert a single color component to hex.
 * @param {number} component - The color component (0-255).
 * @returns {string} The hex string.
 */
const componentToHex = (component: number) => {
  const hexValue = component.toString(16);
  return hexValue.length === 1 ? `0${hexValue}` : hexValue;
};

/**
 * Helper to convert RGB to hex.
 * @param {number} r - Red component.
 * @param {number} g - Green component.
 * @param {number} b - Blue component.
 * @returns {string} The hex color string.
 */
export function rgbToHex(r: number, g: number, b: number) {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`.toUpperCase();
}

/**
 * Clamps a value between 0 and 255.
 * @param {number} value - The value to clamp.
 * @returns {number} The clamped value.
 */
const clamp = (value: number) => Math.max(0, Math.min(255, value));

/**
 * Adjusts brightness of a hex color.
 * @param {string} hex - The hex color string.
 * @param {number} percent - The percentage to adjust.
 * @returns {string} The adjusted hex color string.
 */
export function adjustBrightness(hex: string, percent: number) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  let { r, g, b } = rgb;
  r = Math.floor((r * (100 + percent)) / 100);
  g = Math.floor((g * (100 + percent)) / 100);
  b = Math.floor((b * (100 + percent)) / 100);

  return rgbToHex(clamp(r), clamp(g), clamp(b));
}

/**
 * Sets alpha for a hex color.
 * @param {string} hex - The hex color string.
 * @param {number} alpha - The alpha value.
 * @returns {string} The RGBA color string.
 */
export function setAlpha(hex: string, alpha: number) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Generate prefixed 6-digit random number string.
 * @param prefix - Prefix prepended before dash.
 * @returns String in form `${prefix}-${sixDigitNumber}`.
 */
export function generate6DigitNumber(prefix: string) {
  return `${prefix}-${Math.floor(100000 + Math.random() * 900000)}`;
}

/**
 * Generate prefixed unique string from timestamp + random base36 chars.
 * @param prefix - Prefix prepended before dash.
 * @returns String in form `${prefix}-${base36Timestamp}${randomChars}`.
 */
export function generate6DigitString(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Generates full theme set based on base colors.
 * @param {any} baseConfig - The base configuration.
 * @returns {any} The full theme object.
 */
export function generateFullTheme(baseConfig: any) {
  const modes = ['light', 'dark'];
  const fullTheme: any = {};

  modes.forEach((mode) => {
    const base = baseConfig[mode] || {};

    let primary = '#00A79D';
    if (base.primary) {
      primary = base.primary.startsWith('#') ? base.primary : `#${base.primary}`;
    }

    let secondary = '#212529';
    if (base.secondary) {
      secondary = base.secondary.startsWith('#') ? base.secondary : `#${base.secondary}`;
    }

    const primaryHover = base['primary-color-hover'] || adjustBrightness(primary, -10);
    const halfPrimaryHover = base['half-primary-color-hover'] || adjustBrightness(primary, 20);
    const success = base.success || (mode === 'light' ? '#2e9e3a' : '#16a34a');
    const error = base.error || (mode === 'light' ? '#f04438' : '#dc2626');
    const normalizedPrimary = primary;
    const normalizedSecondary = secondary;

    const defaultGradientGreen = mode === 'light' ? '#6ec282' : '#00a79d';

    fullTheme[mode] = {
      'primary-color': primary,
      'primary-color-hover': primaryHover,
      'half-primary-color-hover': halfPrimaryHover,
      'primary-light': base['primary-light'] || setAlpha(primary, mode === 'light' ? 0.1 : 0.15),
      'secondary-color': secondary,
      success,
      error,
      'tag-error': base['tag-error'] || (mode === 'light' ? '#b3261e' : '#dc2626'),
      'white-color': base['white-color'] || (mode === 'light' ? '#f8f9fa' : '#080d0c'),
      'theme-color': base['theme-color'] || (mode === 'light' ? '#f8f9fa' : '#0d1a17'),
      'pure-white-color': base['pure-white-color'] || (mode === 'light' ? '#ffffff' : '#080d0c'),
      'normal-white': base['normal-white'] || (mode === 'light' ? '#ffffff' : '#080d0c'),
      'black-color': base['black-color'] || (mode === 'light' ? '#000000' : '#f2f5f4'),
      'success-bg': base['success-bg'] || setAlpha(success, mode === 'light' ? 0.1 : 0.15),
      'error-bg': base['error-bg'] || setAlpha(error, mode === 'light' ? 0.2 : 0.15),
      warning: base.warning || (mode === 'light' ? '#f79009' : '#f59e0b'),
      'warning-bg': base['warning-bg'] || setAlpha(mode === 'light' ? '#f79009' : '#f59e0b', mode === 'light' ? 0.1 : 0.15),
      'calender-allow-columns': base['calender-allow-columns'] || setAlpha(primary, 0.2),
      'green-color': base['green-color'] || (mode === 'light' ? normalizedSecondary : '#16a34a'),
      'blue-color': base['blue-color'] || normalizedPrimary,
      'calender-bg': base['calender-bg'] || (mode === 'light' ? '#e3e8f1' : 'rgba(167, 177, 174, 0.15)'),
      'gradient-green': base['gradient-green'] || (base.primary ? primary : defaultGradientGreen),
      'gradient-blue': base['gradient-blue'] || '#0099d6',
      'text-color': base['text-color'] || (mode === 'light' ? '#212529' : '#f2f5f4'),
      'grey-1': base['grey-1'] || (mode === 'light' ? '#6c757d' : '#a7b1ae'),
      'grey-2': base['grey-2'] || (mode === 'light' ? '#a0a7af' : '#a7b1ae'),
      'grey-3': base['grey-3'] || (mode === 'light' ? '#d0d5dd' : 'rgba(167, 177, 174, 0.25)'),
      'grey-4': base['grey-4'] || (mode === 'light' ? '#e9ecef' : 'rgba(167, 177, 174, 0.15)'),
      'grey-5': base['grey-5'] || (mode === 'light' ? '#f9f9f9' : '#080d0c'),
      'button-text-color-1':
        base['button-text-color-1'] || (mode === 'light' ? '#f8f9fa' : '#ffffff'),
      'table-header': base['table-header'] || (mode === 'light' ? '#5e5e5e' : '#a7b1ae'),
      'table-data': base['table-data'] || (mode === 'light' ? '#292d32' : '#f2f5f4'),
      'table-border': base['table-border'] || (mode === 'light' ? '#f4f4f4' : 'rgba(167, 177, 174, 0.15)'),
      'row-bg': base['row-bg'] || (mode === 'light' ? '#ffffff' : '#080d0c'),
      'success-button-bg': base['success-button-bg'] || (mode === 'light' ? primary : '#00a79d'),
      'grey-bg': base['grey-bg'] || (mode === 'light' ? '#ecf5f8' : '#0d1a17'),
      'grey-select-bg':
        base['grey-select-bg'] ||
        (mode === 'light' ? 'rgba(236, 245, 248, 0.5)' : 'rgba(167, 177, 174, 0.12)'),
      'header-bg': base['header-bg'] || (mode === 'light' ? '#ecf5f8b2' : 'rgba(13, 26, 23, 0.85)'),
      'tab-bg': base['tab-bg'] || (mode === 'light' ? '#ecf5f899' : '#0d1a17'),
      'border-grey': base['border-grey'] || (mode === 'light' ? '#e3e8f1' : 'rgba(167, 177, 174, 0.15)'),
      'border-grey-1': base['border-grey-1'] || (mode === 'light' ? '#e9e9ec' : 'rgba(167, 177, 174, 0.15)'),
      'modal-box-body-bg':
        base['modal-box-body-bg'] || (mode === 'light' ? '#e4ebebcf' : '#0d1a17'),
      'modal-box-header-bg':
        base['modal-box-header-bg'] || (mode === 'light' ? '#e9f0f3' : '#080d0c'),
      'modal-box-bg':
        base['modal-box-bg'] || (mode === 'light' ? '#0909094f' : 'rgba(0, 0, 0, 0.7)'),
      'modal-box-container-bg':
        base['modal-box-container-bg'] || (mode === 'light' ? '#f9f9f999' : '#080d0c'),
      'dropdown-bg': base['dropdown-bg'] || (mode === 'light' ? '#f1f7f9d7' : '#080d0c'),
      'selected-bg': base['selected-bg'] || (mode === 'light' ? '#d0d5dd' : 'rgba(167, 177, 174, 0.12)'),
      'border-gradient-color':
        base['border-gradient-color'] ||
        `linear-gradient(to right, ${primaryHover} 0%, ${success} 100%) 1`,
      'card-border-gradient-color':
        base['card-border-gradient-color'] ||
        `linear-gradient(to right, ${primaryHover} 0%, ${success} 100%)`,
      'card-bg-gradient-color':
        base['card-bg-gradient-color'] || (mode === 'light' ? '#eef6f9' : '#161b22'),
      'card-bg-hover-gradient-color':
        base['card-bg-hover-gradient-color'] ||
        (mode === 'light'
          ? `linear-gradient(to top left, ${setAlpha(primary, 0.2)} 0%, ${setAlpha(success, 0.2)} 100%)`
          : `linear-gradient(to top left, rgba(0, 167, 157, 0.15) 0%, rgba(46, 158, 58, 0.15) 100%)`),
      'background-gradient-color':
        base['background-gradient-color'] ||
        (mode === 'light'
          ? `linear-gradient(to top left, ${setAlpha(success, 0.18)} 0%, ${setAlpha(primary, 0.27)} 100%)`
          : `linear-gradient(to top left, rgba(22, 27, 34, 0.9) 0%, rgba(13, 17, 23, 1) 100%)`),
      'multi-select-border':
        base['multi-select-border'] || (mode === 'light' ? '#e9ecef' : '#6c757d'),
      'toggle-bg': base['toggle-bg'] || (mode === 'light' ? '#f9f9f999' : '#252d34b2'),
    };
  });
  return fullTheme;
}
